import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import LoadingComponent from "../layout/share_components/LoadingComponent";
import {
  Box,
  Button,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { User } from "../models/Users/User";
import agent from "../../api/agent";
import PasswordChange from "../models/Users/PasswordChange";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationBox from "../layout/share_components/DeleteConfirmationBox";
import { checkTokenExpiration } from "../../api/tokenExpirationChecking";
import { showToast } from "../layout/share_components/ToastMessage";

interface Props {
  userId: number;
  logout: () => void;
}

interface PasswordValidationErrors {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

export default function UserProfile({ userId, logout }: Props) {
  const [activeTabs, setActiveTabs] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [pwErrors, setPwErrors] = useState<PasswordValidationErrors>({});

  const [userInformation, setUserInformation] = useState<User>({
    userId: 0,
    email: "",
    userName: "",
    phoneNumber: undefined,
  });

  // for third tabs (account delete)
  const [deleteDialogOpenState, setDeleteDialogOpenState] =
    useState<boolean>(false);

  // Authentication
  const [token, setToken] = useState<string>("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        // get the token from localstorage
        const tokenFLocal = localStorage.getItem("token");
        if (tokenFLocal !== null) {
          setToken(tokenFLocal);
          const userResponse = await agent.User.getUserDetail(
            userId,
            tokenFLocal
          );
          setUserInformation(userResponse);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  //Function for change the content when click on the tabs
  function handleTabChange(event: React.SyntheticEvent, selectedValue: number) {
    setActiveTabs(selectedValue);
    // clean the text Field once switch tab
    setPasswords({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPwErrors({});
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setUserInformation({ ...userInformation, [name]: value });
  }

  function handleInputChangeForPasswordChange(
    event: ChangeEvent<HTMLInputElement>
  ) {
    const { name, value } = event.target;
    setPasswords({ ...passwords, [name]: value });
  }

  async function handleUserInfoSubmit(event: FormEvent<HTMLFormElement>) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    event.preventDefault();
    try {
      await agent.User.update(
        userInformation.userId,
        userInformation,
        token
      ).then(async () => {
        const updatedInformation = await agent.User.getUserDetail(
          userId,
          token
        );
        setUserInformation(updatedInformation);
        showToast("Information Updated Successfully", "success");
      });
      setLoading(false);
    } catch (error) {
      console.error("Error updating user:", error);
      showToast("Error Updating Personal Information", "error");
    } finally {
      setLoading(false);
    }
  }

  const validatePassword = (): PasswordValidationErrors => {
    const newErrors: PasswordValidationErrors = {};
    let ClearConfirmPassword = false;

    if (!passwords.currentPassword)
      newErrors.currentPassword = "Current password is required";
    if (!passwords.newPassword)
      newErrors.newPassword = "New password is required";

    // Password validation
    if (passwords.newPassword) {
      if (passwords.newPassword.length < 8) {
        newErrors.newPassword = "Password must be at least 8 characters long";
        ClearConfirmPassword = true;
      } else if (!/(?=.*[a-z])/.test(passwords.newPassword)) {
        newErrors.newPassword =
          "Password must contain at least one lowercase letter";
        ClearConfirmPassword = true;
      } else if (!/(?=.*[A-Z])/.test(passwords.newPassword)) {
        newErrors.newPassword =
          "Password must contain at least one uppercase letter";
        ClearConfirmPassword = true;
      } else if (!/(?=.*\d)/.test(passwords.newPassword)) {
        newErrors.newPassword = "Password must contain at least one number";
        ClearConfirmPassword = true;
      }
    }

    if (passwords.newPassword !== passwords.confirmPassword)
      newErrors.confirmPassword = "Password do not match";

    if (ClearConfirmPassword && passwords.confirmPassword) {
      setPasswords((prev) => ({
        ...prev,
        confirmPassword: "",
      }));
    }
    return newErrors;
  };

  async function handlePasswordChangeSubmit(event: FormEvent<HTMLFormElement>) {
    if (checkTokenExpiration(token)) {
      return;
    }
    event.preventDefault();
    const validationErrors = validatePassword();
    if (Object.keys(validationErrors).length > 0) {
      setPwErrors(validationErrors);
      return;
    }
    const formData: PasswordChange = {
      oldPassword: passwords.currentPassword,
      newPassword: passwords.newPassword,
    };
    setLoading(true);
    try {
      await agent.Authentication.resetPassword(userId, formData, token);
      // clean the text Field and error once success
      setPasswords({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setPwErrors({});

      // toast message to inform user
      showToast("Password Updated Successfully", "success");
    } catch (error) {
      console.error("Password change failed:", error);
      setPwErrors({
        currentPassword:
          "Password change failed. Please check your current password.",
      });
      // showToast("Error Updating Password", "error");
    } finally {
      setLoading(false);
    }
  }

  // Function for third tabs (account delete)
  function handleDeleteOnClick() {
    setDeleteDialogOpenState(true);
  }

  function handleDeleteDialogClose() {
    setDeleteDialogOpenState(false);
  }

  async function handleConfirmDelete() {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.User.delete(userId, token);
      handleDeleteDialogClose();
      // function to logout the user once account delete
      logout();
      showToast("Account Deleted Successfully", "success");
    } catch (error) {
      console.error("Error deleting User:", error);
      showToast("Error Deleting Account", "error");
    } finally {
      setLoading(false);
      handleDeleteDialogClose();
    }
  }

  // Trigger the loader when fetching data from database
  if (loading) return <LoadingComponent content="Loading Data..." />;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        User Profile
      </Typography>

      {/* Tabs Section */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          // orientation="vertical"  //make it vertical
          value={activeTabs}
          onChange={handleTabChange}
          // variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            // value="0"
            //   icon={<People />}
            iconPosition="start"
            label="Personal Information"
            sx={{ textTransform: "none" }}
          />
          <Tab
            // value="0"
            //   icon={<People />}
            iconPosition="start"
            label="Password Change"
            sx={{ textTransform: "none" }}
          />
          <Tab
            // value="1"
            //   icon={<Subscriptions />}
            iconPosition="start"
            label="Account Settings"
            sx={{ textTransform: "none" }}
          />
        </Tabs>
      </Paper>
      {/* Tab Content */}
      {activeTabs === 0 ? (
        <Box component="form" sx={{ mt: 3 }} onSubmit={handleUserInfoSubmit}>
          <TextField
            label="Email"
            name="email"
            value={userInformation.email}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            required
          />

          <TextField
            label="User Name"
            name="userName"
            value={userInformation.userName}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            required
          />

          <TextField
            label="Phone Number"
            name="phoneNumber"
            value={userInformation.phoneNumber}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            type="number"
          />
          <Box
            sx={{ mt: 2, display: "flex", gap: 2, justifyContent: "flex-end" }}
          >
            <Button type="submit" variant="contained" color="primary">
              Save
            </Button>
          </Box>
        </Box>
      ) : activeTabs === 1 ? (
        <Box
          component="form"
          sx={{ mt: 3 }}
          onSubmit={handlePasswordChangeSubmit}
        >
          <TextField
            label="Current Password"
            name="currentPassword"
            type="password"
            value={passwords.currentPassword}
            onChange={handleInputChangeForPasswordChange}
            error={!!pwErrors.currentPassword}
            helperText={pwErrors.currentPassword}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="New Password"
            name="newPassword"
            type="password"
            value={passwords.newPassword}
            onChange={handleInputChangeForPasswordChange}
            error={!!pwErrors.newPassword}
            helperText={pwErrors.newPassword}
            fullWidth
            margin="normal"
            required
          />

          {/* Password requirements hint */}
          {/* <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            Password must contain: at least 8 characters, one lowercase letter,
            one uppercase letter and one number
          </Typography> */}

          <TextField
            label="Reconfirm New Password"
            name="confirmPassword"
            type="password"
            value={passwords.confirmPassword}
            onChange={handleInputChangeForPasswordChange}
            error={!!pwErrors.confirmPassword}
            helperText={pwErrors.confirmPassword}
            fullWidth
            margin="normal"
            required
          />
          <Box
            sx={{ mt: 2, display: "flex", gap: 2, justifyContent: "flex-end" }}
          >
            <Button type="submit" variant="contained" color="primary">
              Save
            </Button>
          </Box>
        </Box>
      ) : (
        <Box textAlign="center">
          <DeleteForeverIcon color="error" style={{ fontSize: 48 }} />
          <Typography variant="h6" gutterBottom>
            Dangerous Zone
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Once you delete your account, there is no going back.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteOnClick}
          >
            Delete Account Permanently
          </Button>

          <DeleteConfirmationBox
            open={deleteDialogOpenState}
            onClose={handleDeleteDialogClose}
            onConfirm={handleConfirmDelete}
          />
        </Box>
      )}
    </Box>
  );
}
